import { AxiosResponse } from "axios";
import { normalize, schema } from 'normalizr';
import { createAsyncThunk } from "@reduxjs/toolkit";
import mineCraftApi from '../../api/mineCraftApi';
import { ReportInterface } from '../../store/mine_craft/reportsSlice';
import { userEntity, usersEntity } from "./usersActions";
import { UserInterface } from "../../store/mine_craft/usersSlice";
import { addonEntity, addonsEntity } from "./addonsActions";
import { AddonInterface } from "../../store/mine_craft/addonsSlice";
import { commentEntity, commentsEntity } from "./commentsActions";
import { CommentInterface } from "../../store/mine_craft/commentsSlice";

function findReportedUserId(report: any) {
  let id;

  switch (report.target_type) {
    case "Addon":
      id = report.addon.user_id;
      break;
    case "User":
      id = report.target_id;
      break;
    case "Comment":
      id = report.comment.sender_id;
      break;
  }

  return id;
}

const processStrategy = (value: any) => {
  return {
    ...value,
    reported_id: findReportedUserId(value)
  };
}

const reportsEntity = new schema.Entity(
  'reports',
  {
    user: usersEntity,
    active_user: usersEntity,
    addon: addonsEntity,
    comment: commentsEntity,
  },
  {
    processStrategy
  }
);

const reportEntity = new schema.Entity(
  'report',
  {
    user: userEntity,
    active_user: userEntity,
    addon: addonEntity,
    comment: commentEntity,
  },
  {
    processStrategy
  }
);

const fetchReportsCollection = async (url: string, params: object = {}) => {
  const response    = await mineCraftApi.get(url, { params })
  const normalized  = normalize<any, {
    users: { [key: string]: UserInterface };
    active_users: { [key: string]: UserInterface };
    addons: { [key: string]: AddonInterface };
    comments: { [key: string]: CommentInterface };
    reports: { [key: string]: ReportInterface };
  }>(response.data.reports, [reportsEntity]);

  return {
    users: normalized.entities.users || {},
    active_users: normalized.entities.active_users || {},
    addons: normalized.entities.addons || {},
    comments: normalized.entities.comments || {},
    reports: normalized.entities?.reports || {},
    ids: normalized.result,
    count: response.data.meta?.total_count
  };
}

const normalizeInstance = (response: AxiosResponse) => {
  const normalized = normalize<any, {
    user: { [key: string]: UserInterface };
    active_user: { [key: string]: UserInterface };
    addon: { [key: string]: AddonInterface };
    comment: { [key: string]: CommentInterface };
    report: ReportInterface;
  }>(response.data.report, reportEntity);
  return normalized.entities;
}

export const fetchReports = createAsyncThunk(
  'reports/fetchReports',
  async (params: object = {}) => fetchReportsCollection('/onesky/reports', params)
);

export const fetchReport = createAsyncThunk(
  'reports/fetchReport',
  async (id: number | string) => {
    const response = await mineCraftApi.get(`/onesky/reports/${id}`);
    return normalizeInstance(response);
  }
);

export const updateReport = createAsyncThunk(
  'reports/updateReport',
  async ({ id, data }: { id: number, data: object }, { rejectWithValue }) => {
    try {
      const response = await mineCraftApi.patch(`/onesky/reports/${id}`, data);
      return normalizeInstance(response);
    } catch (error: any) {
      return rejectWithValue(error.response.data || error)
    }
  }
);

export const destroyReport = createAsyncThunk(
  'reports/destroyReport',
  async (id: number) => {
    const response = await mineCraftApi.delete(`/onesky/reports/${id}`);
    return response.data;
  }
);
